import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { getMaxPointSize } from '../scene-manager.js';
import { API_BASE } from '../api-base.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { PyroLayer, GROUND_Y as PYRO_GROUND_Y, V_WALL as PYRO_V_WALL } from '../particle-layers/pyro-layer.js';
import { ConfettiLayer } from '../particle-layers/confetti-layer.js';
import { laserBillboardChunk, laserBeamFragmentShader } from '../lighting/laser-array.js';
import { Adapt } from './lib/adapt.js';
import { VdjPost, VdjComfort, vdjColor, vdjBeatChroma, VDJ_PRESETS } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';
import { StageScreens, SCREEN, GENERATORS } from './stage/stage-screens.js';
import { StageFigures } from './stage/stage-figures.js';
import { StageCrowd, BEAMS_MAX } from './stage/stage-crowd.js';
import { StageBeams } from './stage/stage-beams.js';

// The stage owns its light: no shared rig (its five randomly mounted heads
// cannot be programmed as a fan of twelve to sixteen lyres).
export const RIG = 'forbidden';

export const PROFILE = {
  id: 'mainstage',
  family: 'stage',
  energyLevel: 0.72,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.50,
  danceability: 0.80,
  tonality: 0.80,
  enabled: true
};

// MAINSTAGE: the main stage of a festival during the set, seen from the
// audience. Everything lives with the music and the drop is the show.
//
//   screens   stage/stage-screens.js: three LED screens and a band under the
//             lip, real VJ content (Mixdeck's generators, the cover, the
//             title, a camera on the DJ), lighting everything else by zone
//   people    stage/stage-figures.js: the DJ behind the console and the
//             dancers on their podiums, articulated silhouettes (R13)
//   crowd     stage/stage-crowd.js: procedural silhouettes by distance, four
//             depth bands, rim-lit by the screens and the beams overhead
//   lights    stage/stage-beams.js: 6 to 16 moving heads, volumetric in the
//             haze, at half resolution; blinders behind the screens
//   lasers    lighting/laser-array.js (LaserCathedral's primitives): sheets
//             over the crowd from the bar before the drop, a tunnel on it
//   pyro      particle-layers/pyro-layer.js in world space at the lip: flame
//             jets on the drop's strong beats and a wall of fire; CO2 columns
//             at the end of the build
//   confetti  particle-layers/confetti-layer.js on the drop, two bars down
//
// THE DIRECTION: a camera shot per bar (front, low, side, stage, drone) with
// a slow move inside each and a kick on the beat; the composition stays on
// the screen's centre (R19). The scenario follows adapt.js and the director:
// beat (bounce, screens hit, heads change position), bar (shot, one screen's
// content), phrase (palette, light program), the BUILD (arms rising, strobe
// accelerating, heads converging on the DJ, the countdown with the title,
// CO2 on the last beat), the DROP (blinders, the collective jump, pyro,
// laser tunnel, confetti, screens at full, the low shot), the CALM (phones
// up, slow wide beams, the cover on the screens, the crowd swaying).
// Every 20 s scene (R20) holds two shots at least, a strong moment (a
// simulated peak when the music has none) and the cover sharp on the centre.
//
// Debug: ?stageshot=front|low|side|stage|drone  ?stagedrop=1 (a drop at 3 s,
// then every 20 s)  ?stagecrowd=<n>  ?stagescreens=mixdeck|cover|title|cam

const DECK_Y = 1.6, DECK_Z0 = 0, DECK_W = 24;
const DJ_POS = new THREE.Vector3(0, DECK_Y, -4.4);
const TIERS = {
  0: { crowd: 400, truss: 4, floor: 2, dancers: 2, lasers: 0, pyro: null, confetti: 0, flags: false },
  1: { crowd: 800, truss: 6, floor: 4, dancers: 2, lasers: 8, pyro: { trails: false, smoke: false }, confetti: 2500, flags: true },
  2: { crowd: 1500, truss: 10, floor: 6, dancers: 4, lasers: 14, pyro: { trails: true, smoke: true }, confetti: 6000, flags: true }
};
const SHOTS = ['front', 'low', 'side', 'stage', 'drone'];
const FALLBACK = [0xff2d8c, 0x2dc8ff, 0xffc02d];
const BUILD_BEATS = 16;
const SCENE_S = 20;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { x = clamp01(x); return x * x * (3 - 2 * x); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function artProxyUrl(u) { return /^data:/i.test(u) ? u : API_BASE + '/api/art-proxy?url=' + encodeURIComponent(u); }

// --- the stage's hardware: silhouettes, lit from behind by the screens ----------------
const propVertex = `
  precision highp float;
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec3 vW; varying vec3 vN; varying vec3 vL; varying float vSafe;
  void main() {
    vL = position;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz; vN = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * w;
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;
const propFragment = `
  precision highp float;
  uniform float uKind;           // 0 plain, 1 console, 2 subwoofers, 3 truss, 4 deck
  uniform vec3 uSize;            // the box's size (local units)
  uniform sampler2D uZones;
  uniform vec3 uLed;             // the LEDs' colour
  uniform float uTime;
  uniform float uBeat;           // 0..1 decaying on the beat
  varying vec3 vW; varying vec3 vN; varying vec3 vL; varying float vSafe;
  void main() {
    vec3 n = normalize(vN);
    vec3 zone = texture2D(uZones, vec2(clamp(vW.x / 30.0 + 0.5, 0.02, 0.98), 0.5)).rgb;
    // the screens are behind and above: faces turned up or back catch them
    vec3 toScreen = normalize(vec3(0.0, 7.5, -9.6) - vW);
    float lit = max(0.0, dot(n, toScreen));
    vec3 col = vec3(0.004, 0.005, 0.008) + zone * (0.09 * lit + 0.02 * max(0.0, n.y));
    // a rim where the face turns away from the camera
    vec3 v = normalize(cameraPosition - vW);
    float rim = pow(1.0 - abs(dot(n, v)), 4.0);
    col += zone * rim * 0.18;
    vec3 q = vL / uSize + 0.5;     // 0..1 in the box
    if (uKind > 0.5 && uKind < 1.5) {
      // console: a row of diodes on the front, platters and knobs on top
      if (n.z > 0.5) {
        vec2 g = fract(vec2(q.x * 26.0, q.y * 6.0)) - 0.5;
        float led = step(length(g), 0.18) * step(0.55, q.y) * step(q.y, 0.75);
        float on = step(0.35, fract(sin(floor(q.x * 26.0) * 12.9898 + floor(uTime * 4.0)) * 43758.5));
        col += uLed * led * (0.25 + 0.75 * on) * 0.8;
      }
      if (n.y > 0.5) {
        vec2 p = vec2(q.x * uSize.x, q.z * uSize.z);
        float pl = min(abs(length(p - vec2(0.75, 0.5)) - 0.33), abs(length(p - vec2(uSize.x - 0.75, 0.5)) - 0.33));
        col += uLed * smoothstep(0.03, 0.0, pl) * 0.35;
        vec2 kg = fract(vec2((p.x - uSize.x * 0.5) * 7.0, p.y * 7.0)) - 0.5;
        float knobs = step(abs(p.x - uSize.x * 0.5), 0.45) * step(length(kg), 0.2);
        col += uLed * knobs * (0.3 + 0.7 * uBeat) * 0.6;
      }
    } else if (uKind > 1.5 && uKind < 2.5) {
      // three cabinets, two cones each, a faint pulse on the kick
      if (n.z > 0.5) {
        vec2 p = vec2(q.x, fract(q.y * 3.0));
        float cone = min(length((p - vec2(0.5, 0.3)) * vec2(1.0, 1.0)), length((p - vec2(0.5, 0.72)) * vec2(1.0, 1.0)));
        col += zone * smoothstep(0.2, 0.17, cone) * (0.03 + 0.05 * uBeat);
        col *= 0.6 + 0.4 * step(0.03, fract(q.y * 3.0));
      }
    } else if (uKind > 2.5 && uKind < 3.5) {
      // the truss: a lattice, the gaps see-through
      float a = abs(fract((vL.x + vL.y + vL.z) * 1.2) - 0.5);
      float b = abs(fract((vL.x - vL.y - vL.z) * 1.2) - 0.5);
      float edge = max(step(0.42, max(abs(q.x - 0.5), abs(q.y - 0.5))), step(0.42, max(abs(q.y - 0.5), abs(q.z - 0.5))));
      if (min(a, b) > 0.07 && edge < 0.5 && max(uSize.x, uSize.y) > 1.0) discard;
    } else if (uKind > 3.5) {
      col *= 0.7;
    }
    gl_FragColor = vec4(col * vSafe, 1.0);
  }
`;

// --- lasers ------------------------------------------------------------------------------
const LASER_EMIT = 3;
const laserVertex = `
  precision highp float;
  attribute float iEmitter;
  attribute float iIndex;
  attribute float iSeed;
  uniform vec3 uEmitter[${LASER_EMIT}];
  uniform float uMode;          // 0 fan sheet, 1 scan, 2 tunnel
  uniform float uPhase;         // 0..1 a bar
  uniform float uPer;           // beams per emitter
  uniform float uGain;
  uniform vec3 uColA, uColB;
  uniform vec3 uTarget;         // the tunnel's aim (the audience, near the camera)
  uniform float uTime;
  varying float vSide; varying float vLen; varying vec3 vColor; varying float vSeed; varying float vGain; varying float vSafeAtt;
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${laserBillboardChunk({ halfWidthNear: 0.012, halfWidthFar: 0.05 })}
  vec3 rotY(vec3 v, float a) { float c = cos(a), s = sin(a); return vec3(v.x * c + v.z * s, v.y, -v.x * s + v.z * c); }
  void main() {
    if (iIndex >= uPer || uGain < 0.002) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 origin = uEmitter[int(iEmitter)];
    float frac = uPer > 1.0 ? iIndex / (uPer - 1.0) - 0.5 : 0.0;
    float ph = uPhase * 6.2831853;
    vec3 dir;
    float len = 48.0;
    if (uMode < 0.5) {
      float lean = iEmitter < 0.5 ? 0.25 : (iEmitter > 1.5 ? -0.25 : 0.0);
      dir = rotY(normalize(vec3(0.0, -0.16, 1.0)), frac * 1.5 * (0.7 + 0.3 * sin(ph)) + lean);
    } else if (uMode < 1.5) {
      float pitch = mix(0.05, 0.42, 0.5 - 0.5 * cos(ph + iEmitter * 1.3));
      dir = rotY(normalize(vec3(0.0, -sin(pitch), cos(pitch))), frac * 0.35 + (iEmitter - 1.0) * 0.3);
    } else {
      // the tunnel: every beam of every emitter on a turning cone around the axis to the audience
      vec3 axis = normalize(uTarget - origin);
      vec3 a = abs(axis.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
      vec3 p1 = normalize(cross(axis, a)), p2 = cross(axis, p1);
      float th = (iIndex + iEmitter * 0.33) / uPer * 6.2831853 + uTime * 1.4;
      dir = normalize(axis + 0.26 * (cos(th) * p1 + sin(th) * p2));
    }
    float t = position.y;
    vec3 along = origin + dir * (t * len);
    float guard;
    vec3 w = laserBillboard(along, dir, t, position.x, guard);
    vSide = position.x; vLen = t; vSeed = iSeed;
    vColor = mod(iIndex, 2.0) < 0.5 ? uColA : uColB;
    vGain = uGain * guard;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;
const laserFragment = laserBeamFragmentShader({ hotMix: 0.55, peakLuminance: 0.9, fadeStart: '0.6', fadeEnd: '1.0' });

// --- CO2 (Bayfront's cannons, here at the lip's ends) ------------------------------------
const CO2_PUFFS = 90;
const co2Vertex = `
  precision highp float;
  attribute float aSeed;
  attribute float aCannon;
  uniform float uTime, uStart, uSizeScale, uPointSizeMax;
  uniform vec2 uCannonX;
  varying float vAlpha;
  void main() {
    float age = uTime - uStart - aSeed * 0.9;
    float life = 1.2;
    if (uStart < 0.0 || age < 0.0 || age > life) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 1.0; vAlpha = 0.0; return; }
    float u = age / life;
    float rise = 10.0 * (1.0 - exp(-age * 3.5));
    float x = (aCannon < 0.5 ? uCannonX.x : uCannonX.y) + (aSeed - 0.5) * (0.3 + 1.8 * u);
    vec3 pos = vec3(x, ${DECK_Y.toFixed(1)} + rise, -0.6 + (fract(aSeed * 7.0) - 0.5) * 1.2);
    vAlpha = (1.0 - u) * (1.0 - u) * 0.5;
    vec4 mv = viewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = clamp((26.0 + 50.0 * u) * uSizeScale / max(-mv.z, 0.5), 2.0, uPointSizeMax);
  }
`;
const co2Fragment = `
  precision mediump float;
  varying float vAlpha;
  void main() {
    if (vAlpha < 0.005) discard;
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    gl_FragColor = vec4(vec3(0.85), exp(-d * d * 3.0) * vAlpha);
  }
`;

// --- blinders: lamps over the screens, a sharp glare each ------------------------------------
const BLINDERS = 10;
const blinderVertex = `
  precision highp float;
  uniform float uLevel;
  uniform float uPx;
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying float vL;
  void main() {
    vec4 mv = viewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    vL = uLevel;
    gl_PointSize = uLevel < 0.01 ? 1.0 : clamp(1.4 * uPx / max(-mv.z, 0.5), 4.0, 90.0);
  }
`;
const blinderFragment = `
  precision mediump float;
  varying float vL;
  void main() {
    float r = length(gl_PointCoord - 0.5) * 2.0;
    float c = (1.0 - smoothstep(0.25, 0.4, r)) * 0.9 + exp(-r * 3.5) * 0.5;
    gl_FragColor = vec4(vec3(1.0, 0.86, 0.62) * c * vL, 1.0);
  }
`;

const _v = new THREE.Vector3(), _v2 = new THREE.Vector3(), _c = new THREE.Color(), _bs = new THREE.Vector2();
const _white = new THREE.Color(1, 1, 1);

export class MainStageVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIERS[this._tier] || TIERS[1];
    const n = parseInt(urlParam('stagecrowd'), 10);
    this._crowdN = Number.isFinite(n) && n > 0 ? n : this._L.crowd;
    const g = urlParam('stagegen');
    this._forceGen = GENERATORS.includes(g) ? g : null;
    const sh = urlParam('stageshot');
    this._forceShot = SHOTS.includes(sh) ? sh : null;
    this._forceDrop = urlParam('stagedrop') === '1';
    const sc = urlParam('stagescreens');
    this._forceScreens = ['mixdeck', 'cover', 'title', 'cam'].includes(sc) ? sc : null;
    this._post = new VdjPost({ bloomStrength: 0.55, bloomThreshold: 0.8, aberration: 0, grain: 0.018, vignette: 0.45, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
  }

  get postProcessing() { return this._post.value; }
  getLightRig() { return null; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    const L = this._L;
    this._objects = [];
    // the screens first: everything else samples their light
    this._screens = new StageScreens(scene, { tier: this._tier });
    const zones = this._screens.zonesTexture;
    this._buildProps(zones);
    // people
    const specs = [{ base: DJ_POS.clone(), kind: 'dj', look: [0, 1, 0, 0.5], zoneU: 0.5, scale: 1 }];
    const podX = L.dancers >= 4 ? [-9.2, -6.4, 6.4, 9.2] : [-7.8, 7.8];
    podX.forEach((x, i) => specs.push({ base: new THREE.Vector3(x, DECK_Y + 0.9, -3.2), kind: 'dancer', style: i % 4, look: [[2, 1, 3, 2][i % 4], 0, 0, i % 2 ? 0.2 : 0.6], zoneU: clamp01(x / 30 + 0.5), scale: 1 }));
    this._figures = new StageFigures(scene, specs, zones);
    // crowd
    this._crowd = new StageCrowd(scene, this._crowdN, zones, { flags: L.flags });
    // heads
    this._beams = new StageBeams(scene, L.truss + L.floor, zones);
    this._heads = [];
    for (let i = 0; i < L.truss; i++) this._heads.push({ base: new THREE.Vector3(-11.5 + 23 * (L.truss > 1 ? i / (L.truss - 1) : 0.5), 12.05, -6), floor: false, dir: new THREE.Vector3(0, -1, 0.3).normalize(), col: new THREE.Color() });
    for (let i = 0; i < L.floor; i++) this._heads.push({ base: new THREE.Vector3(-10.5 + 21 * (L.floor > 1 ? i / (L.floor - 1) : 0.5), DECK_Y + 0.15, -0.6), floor: true, dir: new THREE.Vector3(0, 1, 0.3).normalize(), col: new THREE.Color() });
    if (L.lasers) this._buildLasers();
    if (L.pyro) this._buildPyro();
    this._buildCo2();
    this._buildBlinders();
    // state
    this._time = 0; this._beats = 0; this._lastBeatPhase = null; this._barIndex = 0; this._beatPulse = 0; this._barPhase = 0;
    this._shot = this._forceShot || 'front'; this._shotT = 0; this._shotIdx = 0; this._shotsThisScene = new Set();
    this._palette = [new THREE.Color(FALLBACK[0]), new THREE.Color(FALLBACK[1]), new THREE.Color(FALLBACK[2])];
    this._palKey = null; this._palTurn = 0;
    this._program = 'fan'; this._programTurn = 0;
    this._build = 0; this._buildSim = -1; this._buildFrom = null; this._dropAt = -1e9; this._lastDrop = -1e9; this._pendingDropMs = null;
    this._strong = false; this._coverShown = false; this._sceneN = 0;
    this._blinder = 0; this._co2Fired = false; this._flameBeats = 0;
    this._artUrl = undefined; this._title = undefined; this._coverTex = null;
    this._centreMode = 'mixdeck'; this._sideMode = 0; this._genTurn = 0;
    this._precompiled = false;
    this._shotsLog = [];
  }

  // --- building ------------------------------------------------------------------------------
  _prop(w, h, d, x, y, z, kind, zones) {
    const u = Object.assign({}, safeZoneUniforms, { uKind: { value: kind }, uSize: { value: new THREE.Vector3(w, h, d) }, uZones: { value: zones }, uLed: this._ledCol || (this._ledCol = { value: new THREE.Color(0.2, 0.6, 1) }), uTime: this._propTime || (this._propTime = { value: 0 }), uBeat: this._propBeat || (this._propBeat = { value: 0 }) });
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.ShaderMaterial({ uniforms: u, vertexShader: propVertex, fragmentShader: propFragment }));
    m.position.set(x, y, z);
    this._scene.add(m); this._objects.push(m);
    return m;
  }
  _buildProps(zones) {
    // the deck and its front (the LED band runs along it)
    this._prop(DECK_W, DECK_Y, 10, 0, DECK_Y / 2, -5, 4, zones);
    // the truss: two towers and the header the lyres hang from
    for (const sx of [-1, 1]) this._prop(0.6, 12.6, 0.6, sx * 13.4, 6.3, -6, 3, zones);
    this._prop(27.4, 0.6, 0.6, 0, 12.6, -6, 3, zones);
    this._prop(27.4, 0.6, 0.6, 0, 12.6, -9.9, 3, zones);
    // the console and its podium
    this._prop(3.4, 0.35, 1.4, 0, DECK_Y + 0.175, -3.9, 0, zones);
    this._prop(3.2, 0.95, 0.95, 0, DECK_Y + 0.35 + 0.475, -3.75, 1, zones);
    // podiums
    const podX = this._L.dancers >= 4 ? [-9.2, -6.4, 6.4, 9.2] : [-7.8, 7.8];
    for (const x of podX) this._prop(1.7, 0.9, 1.7, x, DECK_Y + 0.45, -3.2, 0, zones);
    // subwoofer stacks on the ground either side, monitors on the deck's edge
    for (const sx of [-1, 1]) {
      this._prop(1.6, 3.0, 1.3, sx * 13.7, 1.5, -0.4, 2, zones);
      this._prop(1.6, 3.0, 1.3, sx * 15.4, 1.5, -0.6, 2, zones);
    }
    for (const x of [-4.6, -2.2, 2.2, 4.6]) { const m = this._prop(0.9, 0.42, 0.62, x, DECK_Y + 0.21, -0.55, 0, zones); m.rotation.x = -0.45; }
    // cables: a few dark runs on the deck
    for (const x of [-3.2, 1.4, 5.1]) this._prop(0.05, 0.03, 3.6, x, DECK_Y + 0.02, -2.4, 0, zones);
  }
  _buildLasers() {
    const per = this._L.lasers, n = LASER_EMIT * per;
    const geo = new THREE.InstancedBufferGeometry();
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]), 3));
    const e = new Float32Array(n), ix = new Float32Array(n), sd = new Float32Array(n);
    for (let i = 0; i < n; i++) { e[i] = Math.floor(i / per); ix[i] = i % per; sd[i] = Math.random(); }
    geo.setAttribute('iEmitter', new THREE.InstancedBufferAttribute(e, 1));
    geo.setAttribute('iIndex', new THREE.InstancedBufferAttribute(ix, 1));
    geo.setAttribute('iSeed', new THREE.InstancedBufferAttribute(sd, 1));
    geo.instanceCount = n;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this._laserGeo = geo;
    this._laserU = Object.assign({}, safeZoneUniforms, {
      uEmitter: { value: [new THREE.Vector3(-7, 12.0, -5.6), new THREE.Vector3(0, 11.6, -9.2), new THREE.Vector3(7, 12.0, -5.6)] },
      uMode: { value: 0 }, uPhase: { value: 0 }, uPer: { value: per }, uGain: { value: 0 }, uColA: { value: new THREE.Color(0x2dff6a) }, uColB: { value: new THREE.Color(0xff2d8c) },
      uTarget: { value: new THREE.Vector3(0, 2, 20) }, uTime: { value: 0 }, uPixelScale: { value: 800 }, uOuterPx: { value: 5 }, uCorePx: { value: 1.6 }, uPulse: { value: 1 }
    });
    this._laserMat = new THREE.ShaderMaterial({ uniforms: this._laserU, vertexShader: laserVertex, fragmentShader: laserFragment, transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this._lasers = new THREE.Mesh(geo, this._laserMat);
    this._lasers.frustumCulled = false; this._lasers.renderOrder = 30;
    this._scene.add(this._lasers);
  }
  _buildPyro() {
    const f = this._L.pyro;
    this._pyroY = DECK_Y; this._pyroZ = 0.25; this._pyroScale = 4.2;
    this._pyro = new PyroLayer({ max: 3000, trails: f.trails, smoke: f.smoke, reflection: false, floorKill: true, smokeLift: 0.24, crackle: true, flames: true, linewidth: 1.5, space: 'world', world: { x: 0, y: this._pyroY, z: this._pyroZ, scale: this._pyroScale, fog: 0.008 } });
    this._pyro.init(this._scene);
    this._nozzles = [-9.5, -4.2, 4.2, 9.5].map((x) => ({ x: x / this._pyroScale, y: PYRO_GROUND_Y, tilt: 0, dx: 0, dy: 1 }));
    this._wallNozzles = [-11, -7, -3.2, 3.2, 7, 11].map((x) => ({ x: x / this._pyroScale, y: PYRO_GROUND_Y, tilt: 0, dx: 0, dy: 1 }));
    if (this._L.confetti && urlParam('confettioff') !== '1') {
      this._confetti = new ConfettiLayer({ max: this._L.confetti, ribbons: true, unit: 2.0, groundY: 0, fog: 0.008, ambient: 0.12, depthTest: true, renderOrder: 26 });
      this._confetti.init(this._scene);
    }
  }
  _buildCo2() {
    const n = CO2_PUFFS * 2;
    const seeds = new Float32Array(n), cannon = new Float32Array(n);
    for (let i = 0; i < n; i++) { seeds[i] = Math.random(); cannon[i] = i < CO2_PUFFS ? 0 : 1; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    g.setAttribute('aCannon', new THREE.BufferAttribute(cannon, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this._co2Geo = g;
    this._co2U = { uTime: { value: 0 }, uStart: { value: -1 }, uCannonX: { value: new THREE.Vector2(-11.2, 11.2) }, uSizeScale: { value: 30 }, uPointSizeMax: { value: Math.max(1, getMaxPointSize()) } };
    this._co2 = new THREE.Points(g, new THREE.ShaderMaterial({ uniforms: this._co2U, vertexShader: co2Vertex, fragmentShader: co2Fragment, transparent: true, depthWrite: false, depthTest: true }));
    this._co2.frustumCulled = false; this._co2.renderOrder = 24;
    this._scene.add(this._co2);
  }
  _buildBlinders() {
    const p = new Float32Array(BLINDERS * 3);
    const top = SCREEN.centre.y + SCREEN.centre.h / 2 + 0.35;
    for (let i = 0; i < 8; i++) { p[i * 3] = -6 + 12 * i / 7; p[i * 3 + 1] = top; p[i * 3 + 2] = SCREEN.centre.z + 0.2; }
    p[24] = -SCREEN.side.x; p[25] = top; p[26] = SCREEN.side.z + 0.2; p[27] = SCREEN.side.x; p[28] = top; p[29] = SCREEN.side.z + 0.2;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(p, 3));
    this._blinderGeo = g;
    this._blinderU = Object.assign({}, safeZoneUniforms, { uLevel: { value: 0 }, uPx: { value: 600 } });
    this._blinders = new THREE.Points(g, new THREE.ShaderMaterial({ uniforms: this._blinderU, vertexShader: blinderVertex, fragmentShader: blinderFragment, transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending }));
    this._blinders.frustumCulled = false; this._blinders.renderOrder = 27;
    this._scene.add(this._blinders);
  }

  // --- the cover, the title, the palette -----------------------------------------------------
  _loadCover(url) {
    if (!url) { this._screens.setCover(null); return; }
    new THREE.TextureLoader().load(artProxyUrl(url), (tex) => {
      if (!this._scene) { tex.dispose(); return; }
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      const prev = this._coverTex;
      this._coverTex = tex;
      this._screens.setCover(tex);
      if (prev) prev.dispose();
    }, undefined, () => this._screens.setCover(null));
  }
  _applyPalette(profile, turn) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors && pal.vividColors.length ? pal.vividColors : pal.dominantColors)) || FALLBACK;
    const list = src.length ? src : FALLBACK;
    for (let i = 0; i < 3; i++) vdjColor(list[(i + turn) % list.length], this._palette[i]);
    // three colours that must read apart: if two are close in hue, push the third around the wheel
    this._screens.setPalette(this._palette[0], this._palette[1], this._palette[2]);
    if (this._confetti) this._confetti.setPalette(list.slice(0, 4));
    if (this._laserU) { this._laserU.uColA.value.copy(this._palette[(turn + 1) % 3]); this._laserU.uColB.value.copy(this._palette[(turn + 2) % 3]); }
    if (this._crowd && this._crowd.flagUniforms) this._crowd.flagUniforms.uFlagCol.value.forEach((v, i) => { const c = this._palette[i % 3]; v.set(c.r, c.g, c.b); });
    this._ledCol.value.copy(this._palette[1]);
  }

  // --- the music -----------------------------------------------------------------------------
  _beatSeconds(profile) { const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 120; return 60 / Math.max(60, Math.min(190, bpm)); }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return;     // frozen, never black
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    const t = this._time;
    const a = this._adapt.update(dt, profile);
    const director = profile.director || {};
    const bs = this._beatSeconds(profile);
    this._clock.update(dt);
    if (this._clock.wrapped) this._newScene();

    // the beat count, continuous
    const bp = profile.beatPhase != null ? profile.beatPhase : a.beatPhase;
    if (bp != null && this._lastBeatPhase != null) { let d = bp - this._lastBeatPhase; if (d < -0.5) d += 1; this._beats += Math.max(0, d); }
    else this._beats += dt / bs;
    this._lastBeatPhase = bp;
    const beatPhase = this._beats % 1;
    // beats, bars and phrases counted here, from the beat phase (or the clock): they keep coming without a grid
    const beatN = Math.floor(this._beats), barN = Math.floor(this._beats / 4), phraseN = Math.floor(this._beats / 16);
    const isBeat = this._beatN != null && beatN !== this._beatN;
    const isBar = this._barN != null && barN !== this._barN;
    const isPhrase = this._phraseN != null && phraseN !== this._phraseN;
    this._beatN = beatN; this._barN = barN; this._phraseN = phraseN;
    if (isBeat) this._beatPulse = 1; else this._beatPulse *= Math.exp(-dt * 7);
    this._barPhase = (this._beats / 4) % 1;

    // track: cover, title, palette
    if (profile.artworkUrl !== this._artUrl) { this._artUrl = profile.artworkUrl; this._loadCover(profile.artworkUrl); }
    const title = profile.title || '';
    if (title !== this._title) { this._title = title; this._screens.setTitle(title); }
    const palKey = profile.artworkPalette ? String(profile.artworkPalette.vividColors || '') : '';
    if (palKey !== this._palKey) { this._palKey = palKey; this._applyPalette(profile, this._palTurn); }

    // --- build and drop
    const ev = director.nextEvent;
    let dropNow = !!a.drop, buildReal = 0;
    if (ev && ev.type === 'drop' && ev.msUntil != null) {
      const beatsOut = ev.msUntil / 1000 / bs;
      buildReal = clamp01(1 - beatsOut / BUILD_BEATS);
      if (ev.msUntil <= dt * 500 + 30) dropNow = true;
      if (this._pendingDropMs != null && this._pendingDropMs < 400 && ev.msUntil > this._pendingDropMs + 250) dropNow = true;
      this._pendingDropMs = ev.msUntil;
    } else {
      if (this._pendingDropMs != null && this._pendingDropMs < 400) dropNow = true;
      this._pendingDropMs = null;
    }
    if (director.mode === 'build') { this._buildFrom = this._buildFrom == null ? t : this._buildFrom; buildReal = Math.max(buildReal, clamp01((t - this._buildFrom) / (BUILD_BEATS * bs))); }
    else this._buildFrom = null;
    // the forced and simulated drops (R20: a strong moment in every scene)
    if (this._forceDrop && (t > 2 && this._lastDrop < 0 || t - this._lastDrop > SCENE_S) && this._buildSim < 0) { this._buildSim = t; this._buildSimBeats = BUILD_BEATS; }
    const ct = this._clock.t;
    if (!this._strong && ct > SCENE_S - 9 && this._buildSim < 0 && buildReal === 0 && t - this._lastDrop > 8) { this._buildSim = t; this._buildSimBeats = 8; }
    let build = buildReal;
    if (this._buildSim >= 0) {
      const p = (t - this._buildSim) / (this._buildSimBeats * bs);
      build = Math.max(build, clamp01(p));
      if (p >= 1) { dropNow = true; this._buildSim = -1; }
    }
    if (build > 0.05) this._strong = true;
    if (dropNow && t - this._lastDrop > 2) this._drop(t);
    // CO2 on the last beat of a build
    if (build > 0.9 && !this._co2Fired) { this._co2Fired = true; this._co2U.uStart.value = t; }
    if (build < 0.2) this._co2Fired = false;
    this._build = build;
    const sinceDrop = t - this._lastDrop;
    const dropOn = sinceDrop < 8 * bs;                  // two bars of drop
    const calm = !dropOn && build < 0.05 && a.intensity < 0.5;
    this._calm = calm; this._dropOn = dropOn;

    // --- bar and phrase
    if (isBar) this._onBar(dropOn, build, calm);
    if (isPhrase) { this._palTurn++; this._applyPalette(profile, this._palTurn); this._programTurn++; }
    if (isBeat && dropOn && this._pyro && this._flameBeats < 4 && Math.floor(this._beats) % 2 === 0) { this._flame(); this._flameBeats++; }

    // --- people
    const armUp = smooth((build - 0.3) / 0.5);
    const armsUp = dropOn ? Math.exp(-sinceDrop / (6 * bs)) : 0;
    const jump = sinceDrop >= 0 && sinceDrop < 0.5 ? Math.sin(Math.PI * sinceDrop / 0.5) * 0.35 : 0;
    const m = { beats: this._beats, beatPhase, bar: this._barPhase, barIndex: this._barIndex, armUp, armsUp, jump, nod: calm ? 0.4 : 1, t };
    const prof = this._shot === 'side' ? 1 : 0;
    const spotDJ = new THREE.Vector3();
    if (build > 0.3) spotDJ.set(this._palette[2].r, this._palette[2].g, this._palette[2].b).multiplyScalar(0.6 * build);
    this._figures.update(m, prof, [spotDJ]);
    const dj = this._figures.dj;
    if (dj) this._screens.setDJ(dj.J);
    const CU = this._crowd.uniforms;
    CU.uBeats.value = this._beats; CU.uTime.value = t; CU.uBounce.value = calm ? 0.35 : 1;
    CU.uSway.value = calm ? 1 : 0; CU.uArms.value = Math.max(build * 0.95, armsUp * 0.9); CU.uPhones.value += ((calm ? 1 : 0.15) - CU.uPhones.value) * (1 - Math.exp(-dt * 1.5));
    CU.uRimGain.value = 1.4;
    // the crowd's faces are lit when the camera looks at them from the stage side
    CU.uFrontLit.value = this._camera && this._camera.position.z < 2 ? 1 : 0;

    // --- lights
    this._updateHeads(dt, t, build, dropOn, calm, bs);
    if (this._lasers) this._updateLasers(t, build, dropOn, sinceDrop, bs);
    if (this._pyro) this._updatePyro(dt, a);
    this._co2U.uTime.value = t;
    // blinders: the drop's first beat, bounded (R10)
    this._blinder *= Math.exp(-dt / 0.3);
    this._blinderU.uLevel.value = this._blinder;
    this._beams.compU.uVeil.value = 0.2 * this._blinder;

    // --- screens
    this._updateScreens(t, a, director, build, dropOn, calm, bs);
    this._propTime.value = t; this._propBeat.value = this._beatPulse;

    // --- camera
    this._updateCamera(dt, t);
    // post: the party preset's chroma on the beat
    this._post.update(vdjBeatChroma(beatPhase, bs) * (calm ? 0.3 : 1));
  }

  _newScene() {
    this._sceneN++;
    this._strong = false; this._coverShown = false; this._shotsThisScene = new Set([this._shot]);
  }

  _drop(t) {
    this._lastDrop = t; this._dropAt = t;
    this._strong = true;
    if (this._comfort.requestFlash(t)) this._blinder = 1;
    this._crowd.uniforms.uJumpT.value = t;
    this._flameBeats = 0;
    if (this._pyro) {
      this._pyro.fireWall(this._wallNozzles, PYRO_V_WALL[0], PYRO_V_WALL[1], 260);
      this._flame();
    }
    if (this._confetti) {
      const cannons = [-8, -3, 3, 8].map((x) => ({ origin: new THREE.Vector3(x, DECK_Y, 0.3), direction: new THREE.Vector3(-x * 0.02, 1, 0.45).normalize() }));
      for (const c of cannons) this._confetti.fire(c.origin, c.direction, 1.15, 1.0);
      this._confetti.fireRibbons(cannons);
      this._confetti.setFlash(1);
    }
    if (!this._forceShot) { this._shot = 'low'; this._shotT = 0; this._shotsThisScene.add('low'); }
    this._shotsLog.push({ t: +t.toFixed(2), shot: 'low', why: 'drop' });
  }
  _flame() {
    if (!this._pyro) return;
    for (const n of this._nozzles) this._pyro.fireFlame(n);
  }

  _onBar(dropOn, build, calm) {
    this._barIndex++;
    // the next shot (not on the drop's first bar: the low shot holds)
    if (!this._forceShot && !(dropOn && this._time - this._lastDrop < 1.5)) {
      let pool = SHOTS.slice();
      if (calm) pool = ['front', 'drone', 'stage', 'side'];
      if (build > 0.05) pool = ['front', 'side', 'stage', 'low'];
      pool = pool.filter((s) => s !== this._shot);
      // a shot not yet seen in this scene first
      const fresh = pool.filter((s) => !this._shotsThisScene.has(s));
      const pick = (fresh.length ? fresh : pool)[Math.floor(Math.random() * (fresh.length ? fresh.length : pool.length))];
      this._shot = pick; this._shotT = 0; this._shotsThisScene.add(pick);
      this._shotsLog.push({ t: +this._time.toFixed(2), shot: pick });
      if (this._shotsLog.length > 200) this._shotsLog.shift();
    }
    // one screen's content
    this._genTurn++;
    this._screens.generator = GENERATORS[this._genTurn % GENERATORS.length];
    this._sideMode = (this._sideMode + 1 + (Math.random() < 0.5 ? 1 : 0)) % 3;
    this._screens.panelU.uSideMap.value = this._sideMode === 1 ? 1 : 0;
  }

  _updateScreens(t, a, director, build, dropOn, calm, bs) {
    const S = this._screens, B = S.bus;
    B.uTime.value = t; B.uBeats.value = this._beats; B.uBeatPhase.value = this._beats % 1; B.uBarPhase.value = this._barPhase;
    B.uPulse.value = this._beatPulse; B.uEnergy.value = a.intensity; B.uBarIndex.value = this._barIndex;
    const bands = director.bands || a.bands;
    if (bands) B.uBands.value.set(bands.low != null ? bands.low : 0.5, bands.mid != null ? bands.mid : 0.4, bands.high != null ? bands.high : 0.3);
    else B.uBands.value.set(0.45 + 0.35 * this._beatPulse, 0.4 + 0.2 * Math.sin(t * 3), 0.3 + 0.2 * Math.sin(t * 5.3));
    // the centre: the cover once per scene at least (R20), on the calm, else a generator; the title on the build, with the countdown
    const ct = this._clock.t;
    let centre = 'mixdeck';
    if (calm) centre = 'cover';
    else if (!this._coverShown && ct > 5 && ct < SCENE_S - 6 && build < 0.05 && !dropOn) centre = 'cover';
    else if (build > 0.05) centre = 'title';
    else if (this._barIndex % 6 === 3) centre = 'title';
    if (this._forceScreens) centre = this._forceScreens === 'cam' ? 'mixdeck' : this._forceScreens;
    if (centre === 'cover' && this._coverTex) { this._coverHold = (this._coverHold || 0) + 1 / 60; if (this._coverHold > 2.5) this._coverShown = true; }
    else this._coverHold = 0;
    const set = (p, mode) => S.setMode(p, mode === 'cover' ? [0, 1, 0, 0] : mode === 'title' ? [0, 0, 1, 0] : mode === 'cam' ? [0, 0, 0, 1] : [1, 0, 0, 0]);
    set(0, centre);
    // the sides: continuation / mirror of the content, or the camera on the DJ; the title with the centre's countdown
    let side = this._sideMode === 2 ? 'cam' : 'mixdeck';
    if (build > 0.05) side = 'title';
    if (calm) side = this._sideMode === 2 ? 'cam' : 'cover';
    if (this._forceScreens === 'cam') side = 'cam';
    else if (this._forceScreens) side = this._forceScreens;
    set(1, side); set(2, side); set(3, 'mixdeck');
    // the countdown: the last four beats of the build
    const left = build > 0 ? Math.ceil((1 - build) * BUILD_BEATS) : 0;
    S.panelU.uCount.value = build > 0.05 && left >= 1 && left <= 4 ? left : 0;
    // brightness: hits on the beat, full on the drop, lower in the calm
    const base = dropOn ? 1.15 : calm ? 0.7 : 0.9;
    S.panelU.uBright.value = base * (1 + 0.22 * this._beatPulse);
    if (dropOn && !this._forceScreens) S.generator = (Math.floor(this._beats) % 2) ? 'shapes' : 'tunnel';
    if (this._forceGen) S.generator = this._forceGen;
    if (!this._precompiled && this._camera) this._precompiled = true;
  }

  // the moving heads: programs per phrase, a step on every beat, convergence on the build, strobe
  _updateHeads(dt, t, build, dropOn, calm, bs) {
    const H = this._heads, B = this._beams.heads, n = H.length;
    const programs = ['fan', 'ballyhoo', 'cross', 'crowd'];
    let prog = programs[this._programTurn % programs.length];
    if (calm) prog = 'calm';
    if (build > 0.35) prog = 'converge';
    if (dropOn) prog = 'ballyhoo';
    this._program = prog;
    const step = Math.floor(this._beats);
    const djHead = _v2.copy(DJ_POS).add(new THREE.Vector3(0, 1.7, 0));
    const strobeHz = build > 0.5 ? 2 + 6 * (build - 0.5) * 2 : 0;
    const strobe = strobeHz > 0 ? (Math.sin(t * strobeHz * Math.PI * 2) > 0.6 ? 1 : 0) : 0;
    for (let i = 0; i < n; i++) {
      const h = H[i], f = n > 1 ? i / (n - 1) - 0.5 : 0, side = Math.sign(h.base.x) || 1;
      const target = new THREE.Vector3();
      if (prog === 'converge') target.copy(djHead).sub(h.base);
      else if (h.floor) {
        // floor heads: up into the haze, fanned
        const k = prog === 'calm' ? 0.25 : 0.45 * Math.sin(t * 0.8 + i) ;
        target.set(f * 1.6 + k, 1, 0.25 + 0.2 * Math.sin(step * 1.3 + i));
      } else if (prog === 'fan') {
        const open = 0.6 + 0.5 * ((step % 4) / 3);
        target.set(f * open * 2.2, -1, 0.7);
      } else if (prog === 'ballyhoo') {
        const sp = dropOn ? 2.2 : 1.2;
        target.set(Math.sin(t * sp + i * 0.7) * 0.9, -1, 0.55 + 0.45 * Math.sin(t * sp * 1.3 + i));
      } else if (prog === 'cross') {
        const sw = step % 2 === 0 ? 1 : -1;
        target.set(-side * 0.8 * sw, -1, 0.8);
      } else if (prog === 'crowd') {
        target.set(f * 0.8 + 0.3 * Math.sin(t * 0.9 + i), -0.6, 1.2 + 0.4 * Math.sin(step * 0.9 + i));
      } else {
        target.set(f * 2.4, 0.35, 0.4 + 0.2 * Math.sin(t * 0.3 + i));       // calm: wide, slow, into the air
      }
      target.normalize();
      const k = 1 - Math.exp(-dt / (prog === 'calm' ? 1.2 : prog === 'ballyhoo' ? 0.08 : 0.14));
      h.dir.lerp(target, k).normalize();
      // colour: the palette in turn; white on the strobe
      const c = this._palette[(i + this._programTurn) % 3];
      let level = calm ? 0.55 : dropOn ? 1.1 : 0.85;
      level *= 0.8 + 0.2 * this._beatPulse;
      h.col.copy(c);
      if (strobe) { h.col.lerp(_white, 0.85); level = 1.2; }
      B.O[i].copy(h.base); B.D[i].copy(h.dir);
      B.C[i].set(h.col.r, h.col.g, h.col.b, level);
      B.SP[i] = calm ? 0.06 : prog === 'converge' ? 0.022 : 0.032;
    }
    this._beams.uniforms.uTime.value = t;
    this._beams.uniforms.uHaze.value = calm ? 0.8 : 1;
    // the crowd and the confetti see the same beams
    const CU = this._crowd.uniforms;
    for (let i = 0; i < Math.min(n, BEAMS_MAX); i++) {
      CU.uBeamO.value[i].copy(B.O[i]); CU.uBeamD.value[i].copy(B.D[i]);
      CU.uBeamC.value[i].set(B.C[i].x, B.C[i].y, B.C[i].z).multiplyScalar(B.C[i].w * 0.35);
    }
    CU.uBeamN.value = Math.min(n, BEAMS_MAX);
    if (this._confetti) for (let i = 0; i < 6; i++) { const j = Math.floor(i * n / 6); this._confetti.setLight(i, B.O[j], B.D[j], H[j].col, B.C[j].w); }
  }

  _updateLasers(t, build, dropOn, sinceDrop, bs) {
    const U = this._laserU;
    U.uTime.value = t;
    U.uPhase.value = this._barPhase;
    // from the bar before the drop (the last four beats of the build), a tunnel on the drop
    let g = 0;
    if (build > 0.75) { g = 0.6; U.uMode.value = 1; }
    if (dropOn) { g = 0.9; U.uMode.value = sinceDrop < 4 * bs ? 2 : 0; }
    U.uGain.value += (g - U.uGain.value) * 0.25;
    if (this._camera) U.uTarget.value.copy(this._camera.position).add(_v.set(0, -1.5, 0));
  }

  _updatePyro(dt, a) {
    const bands = (a && a.bands) || null;
    this._pyro.update(dt, bands ? bands.high || 0.3 : 0.3, 0.5);
    if (this._confetti) { this._confetti.setCamera(this._camera); this._confetti.update(dt, bands ? bands.high || 0.3 : 0.3); }
  }

  // --- the camera -----------------------------------------------------------------------------
  _updateCamera(dt, t) {
    const cam = this._camera;
    if (!cam) return;
    this._shotT += dt;
    const u = clamp01(this._shotT / 5);
    let pos, look, fov;
    switch (this._shot) {
      case 'low': pos = [0.8 - 1.6 * u, 1.75, 9.2 - 0.5 * u]; look = [0, 6.6, -8]; fov = 60; break;
      case 'side': pos = [19.5, 5.4 + 0.5 * u, -2.6 + 1.6 * u]; look = [0, 3.6, -3.4]; fov = 50; break;
      case 'stage': pos = [1.2 - 2.4 * u, 5.0, -8.4]; look = [0, 2.0, 22]; fov = 62; break;
      case 'drone': pos = [0, 17 - 2.5 * u, 52 - 10 * u]; look = [0, 4.5, -4]; fov = 50; break;
      default: pos = [-1.6 + 3.2 * u, 4.6, 43]; look = [0, 6.4, -6]; fov = 46;
    }
    // the kick: a small push on the beat
    const kick = this._beatPulse * (this._calm ? 0.2 : 1);
    cam.position.set(pos[0], pos[1] + kick * 0.04, pos[2]);
    cam.lookAt(look[0], look[1], look[2]);
    cam.fov = fov * (1 - 0.015 * kick);
    cam.near = 0.1; cam.far = 200;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
    // where the screens are on screen, for the haze around them
    _v.set(0, SCREEN.centre.y, SCREEN.centre.z).project(cam);
    this._beams.compU.uScrNdc.value.set(_v.z < 1 ? _v.x : 0, _v.z < 1 ? _v.y : 2);
    _v2.set(SCREEN.centre.w / 2, SCREEN.centre.y, SCREEN.centre.z).project(cam);
    this._beams.compU.uScrR.value = Math.max(0.15, Math.abs(_v2.x - _v.x) * 1.2);
    const h = this._lastH || 720;
    this._blinderU.uPx.value = h / (2 * Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2));
    if (this._lasers) this._laserU.uPixelScale.value = this._blinderU.uPx.value;
  }

  get probe() {
    return {
      shot: this._shot, program: this._program, build: +this._build.toFixed(2), dropOn: !!this._dropOn, calm: !!this._calm,
      scene: this._sceneN, sceneT: +this._clock.t.toFixed(2), strong: this._strong, coverShown: this._coverShown,
      gen: this._screens.generator, modes: this._screens.panelU.uModes.value.map((v) => v.toArray().join('')).join('|'),
      lastDrop: +this._lastDrop.toFixed(2), crowd: this._crowd.count, heads: this._heads.length, blinder: +this._blinder.toFixed(2),
      laser: this._laserU ? +this._laserU.uGain.value.toFixed(2) : null, shots: this._shotsLog.slice(-12)
    };
  }

  onTrackChange() {}

  dispose() {
    const s = this._scene;
    if (!s) return;
    for (const m of this._objects) { s.remove(m); m.geometry.dispose(); m.material.dispose(); }
    this._screens.dispose(); this._figures.dispose(); this._crowd.dispose(); this._beams.dispose();
    if (this._lasers) { s.remove(this._lasers); this._laserMat.dispose(); this._laserGeo.dispose(); }
    if (this._pyro) this._pyro.dispose();
    if (this._confetti) { this._confetti.dispose(); }
    s.remove(this._co2); this._co2.material.dispose(); this._co2Geo.dispose();
    s.remove(this._blinders); this._blinders.material.dispose(); this._blinderGeo.dispose();
    if (this._coverTex) this._coverTex.dispose();
    this._scene = null;
  }
}
